import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { DownArrowIcon, RightArrowIcon } from "assets";
import { Menus } from "./ManuList";
import { useUser } from "utils/helpers/UserContext";
import { SuperAdminStatusEnum } from '../../props/enum';

const MainSideBar: React.FC<any> = ({ isOpen }) => {
  const { authUser, action, superAdmin } = useUser();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    {}
  );
  const userPermissions: string[] = action || [];

  // Recursive filter function
  const filterMenus = (menus: any[]): any[] => {
    return menus
      .map((menu) => {
        if( superAdmin === SuperAdminStatusEnum.SuperAdmin ) {
          return menu; // SuperAdmin has access to all menus
        }
        if (menu.permission) {
          const requiredPermissions = Array.isArray(menu.permission)
            ? menu.permission
            : [menu.permission];

          const hasPermission = requiredPermissions.some((p: any) =>
            userPermissions.includes(p) 
          );

          if (!hasPermission) {
            return null;
          }
        }
        // If menu has children, filter them recursively
        if (menu.children) {
          const filteredChildren = filterMenus(menu.children);
          // If no children left after filtering, and no ref, hide this menu
          if (filteredChildren.length === 0 && !menu.ref) {
            return null;
          }
          return { ...menu, children: filteredChildren };
        }
        return menu;
      })
      .filter(Boolean);
  };

  // Use filtered menus for rendering
  const filteredMenus = filterMenus(Menus);
  const toggleDropdown = (key: string) => {
    setExpandedMenus((prev) => {
      const isCurrentlyOpen = prev[key];

      const updated: Record<string, boolean> = {};

      if (isCurrentlyOpen) {
        for (const k in prev) {
          if (!(k === key || k.startsWith(`${key}-`))) {
            updated[k] = prev[k];
          }
        }
        return updated;
      } else {
        const topLevelKey = key.split("-")[0];

        const parts = key.split("-");
        let currentKey = "";
        for (let i = 0; i < parts.length; i++) {
          currentKey = i === 0 ? parts[i] : `${currentKey}-${parts[i]}`;
          updated[currentKey] = true;
        }
        return Object.fromEntries(
          Object.entries(updated).filter(([k]) => k.startsWith(topLevelKey))
        );
      }
    });
  };

  const checkActive = (menu: any): boolean => {
    if (menu.ref && menu.ref !== "" && location.pathname.startsWith(menu.ref)) {
      return true;
    }
    if (menu.children) {
      return menu.children.some((child: any) => checkActive(child));
    }
    return false;
  };
  useEffect(() => {
    const expandParents = (menus: any[], parentKey = "") => {
      menus.forEach((menu) => {
        const key = parentKey ? `${parentKey}-${menu.title}` : menu.title;
        if (checkActive(menu)) {
          setExpandedMenus((prev) => ({ ...prev, [key]: true }));
        }
        if (menu.children) {
          expandParents(menu.children, key);
        }
      });
    };
    expandParents(Menus);
  }, [location.pathname]);

  const renderMenu = (menu: any, parentKey: string = "") => {
    const key = parentKey ? `${parentKey}-${menu.title}` : menu.title;
    const isActive = checkActive(menu);

    return (
      <li className=" " key={key}>
        <div>
          <Link
            to={menu.ref || "#"}
            className={`group flex w-full items-center justify-between ${
              isOpen ? "flex-row gap-4 font-bold px-4" : "flex-col px-2"
            } hover:bg-primary-500 rounded-lg py-2 transition-all duration-300 hover:text-white text-primary-500 ease-in-out ${
              isActive ? "bg-primary-500 text-white" : "text-primary-500"
            }`}
            onClick={(e) => {
              if (menu.children) {
                e.preventDefault();
                toggleDropdown(key);
              } else {
                setExpandedMenus({});
              }
            }}
          >
            {menu.icon && (
              <menu.icon
                className={`${isOpen ? "size-5" : "size-4"} ${
                  isActive ? "text-white" : "group-hover:text-white"
                }`}
                style={{ flexShrink: 0 }}
              />
            )}
            <span
              className={`flex-1 font-medium ${
                isOpen ? "text-sm text-start" : "text-[9px] text-center"
              } ${
                isActive ? "text-white" : "group-hover:text-white"
              } transition-all duration-300`}
              style={{ display: "flex", alignItems: "center" }}
            >
              {menu.title}
            </span>

            {menu.children && (
              <span
                className={`size-4 ${
                  isActive ? "text-white" : "group-hover:text-white"
                }`}
                style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                {expandedMenus[key] ? <DownArrowIcon /> : <RightArrowIcon />}
              </span>
            )}
          </Link>
          {menu.children && (
            <ul
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedMenus[key]
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              } ${isOpen ? "ml-8" : "ml-0"}`}
            >
              {menu.children.map((child: any) => renderMenu(child, key))}
            </ul>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="flex flex-col overflow-auto h-[calc(100vh-67px)] w-full border border-primary-100 bg-white hide-scrollbar shadow-sm rounded-b-md pt-5 px-2">
      <div className="flex-1">
        <ul className="space-y-1">
          {filteredMenus.map((menu) => renderMenu(menu))}
        </ul>
      </div>

      <p className=" border-t border-primary-500 text-[10px] text-center text-primary-500 pt-2 pb-4">
        © {new Date().getFullYear()} iBEST Technologies. All rights reserved.
      </p>
    </div>
  );
};

export default MainSideBar;
